import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';

import {
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@material-ui/core';

import SaveButton from 'app/components/SaveButton';
import SearchIcon from '@material-ui/icons/Search';

import notification from 'app/config/notification';

import { SERVICES_CODES } from 'app/config/services';
import { USER_SECTORS } from 'app/utils/constants';
import serviceEndpoints from 'app/api/services/serviceEndpoints';

const ServiceData = (props) => {
    const { classes, service } = props;

    return (
        <Grid container spacing={0} className={classes.requestResult}>
            <Grid item xs={12}>
                <b>{service.nmServico}</b> <br />
                {service.deServico}
            </Grid>
        </Grid>
    );
};

const ServiceSelection = (props) => {
    const { classes, intl, handleSubmit, handleCancel } = props;
    const internalNewRequest = useSelector((state) => state.forms.internalNewRequest);
    const user = useSelector((state) => state.auth.user);

    const isDplo = useMemo(() => {
        return !user.setores ? null : user.setores.find((e) => USER_SECTORS.DQS_DPLO === e.cdOrgaoSetor);
    }, [user]);

    const [serviceList, setServiceList] = useState([]);

    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [cdServico, setCdServico] = useState(null);

    const handleChange = (event) => {
        setCdServico(event.target.value);
    };

    useEffect(() => {
        const servicesToBeExcluded = [SERVICES_CODES.ACESSO_PORTAL_FISICA, SERVICES_CODES.ACESSO_PORTAL_JURIDICA];

        (async () => {
            setLoading(true);
            let doc = isDplo ? user.username : internalNewRequest.cdUsuario;
            if (internalNewRequest.company) {
                doc = internalNewRequest.company.deCnpjMatriz ?? internalNewRequest.company.deCpfCnpj ?? doc;
            }
            const services = await serviceEndpoints.getServicesByUser(doc);
            if (!services?.data.length > 0) {
                notification(intl.formatMessage({ id: 'MESSAGES.REQUEST.PROBLEM_GET_SERVICES' }), {
                    toastId: 'PROBLEM_GET_SERVICES',
                    type: 'error',
                });
                setLoading(false);
                return false;
            }

            const filteredServices = services.data.filter(
                (service) => !servicesToBeExcluded.includes(service.cdServico),
            );
            const ordenedServices = _.orderBy(filteredServices, ['nmServico']);
            setServiceList(ordenedServices);
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = async () => {
        if (search !== '') {
            const found = serviceList.filter((serv) => {
                return (
                    serv.nmServico.toLowerCase().indexOf(search.toLowerCase()) >= 0 ||
                    serv.deServico.toLowerCase().indexOf(search.toLowerCase()) >= 0
                );
            });
            setServiceList(found);
        } else {
            setServiceList(serviceList);
        }
    };

    useEffect(() => {
        (async () => {
            if (serviceList.length === 0) {
                return false;
            }
            setLoading(true);
            const rowsTemp = [];
            let index = 0;
            for (const serv of serviceList) {
                const rowsPrepared = await prepareData(serv, index);
                rowsTemp.push(rowsPrepared);
                index++;
            }
            setRows(rowsTemp);
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceList]);

    const prepareData = async (content, i) => {
        return (
            <FormControlLabel
                key={`req-${i}`}
                className={classes.radio}
                value={String(content.cdServico)}
                control={<Radio />}
                label={<ServiceData service={content} classes={classes} />}
            />
        );
    };

    return (
        <Grid container spacing={3} className={classes.containerForm}>
            <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                    <InputLabel htmlFor="outlined-adornment-search">Digite o que procura</InputLabel>
                    <OutlinedInput
                        id="outlined-adornment-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={(e) => handleSearch()}
                                    onMouseDown={() => {}}
                                    edge="end">
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        }
                        labelWidth={400}
                    />
                </FormControl>
            </Grid>

            {loading && (
                <Grid item xs={12} className={classes.centerProgress}>
                    <CircularProgress size={18} />
                </Grid>
            )}

            {!loading && (
                <Grid item xs={12}>
                    {rows && rows.length > 0 && (
                        <Grid container spacing={0}>
                            <Grid item xs={12}>
                                <FormControl fullWidth component="fieldset">
                                    <RadioGroup
                                        aria-label="cdServico"
                                        name="cdServico"
                                        value={cdServico}
                                        onChange={handleChange}>
                                        <Table>
                                            <TableBody>
                                                {rows.map((row, index) => (
                                                    <TableRow key={`service-${index}`}>
                                                        <TableCell>{row}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}

                    {(!serviceList || serviceList.length === 0) && (
                        <Grid item xs={12} className={classes.noResults}>
                            Nenhum serviço encontrado
                        </Grid>
                    )}
                </Grid>
            )}
            <SaveButton
                mainColor={'primary'}
                hideActionBtn={false}
                isInternalForm={true}
                canEdit={true}
                onCancel={() => {
                    handleCancel('cdServico');
                }}
                onSave={() => {
                    if (cdServico) {
                        const service = serviceList.filter((serv) => serv.cdServico === Number(cdServico));
                        handleSubmit('cdServico', cdServico, service[0]);
                    } else {
                        notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.NO_SERVICE_SELECTED' }), {
                            toastId: 'NO_SERVICE_SELECTED',
                            type: 'warning',
                        });
                    }
                }}
                cancelLabel={'Cancelar'}
                saveLabel={'Confirmar'}
            />
        </Grid>
    );
};

export default ServiceSelection;
